/**
 * Corporate Identity Admin — Issue 1 (Owner UAT)
 * ==============================================
 * Editable admin surface for the canonical `legal_entities` +
 * `treatx_corporate_certificates` SSOTs. Reuses the existing endpoints:
 *
 *   GET  /api/admin/legal-entities                — list
 *   PUT  /api/admin/legal-entities/{entity_id}    — update
 *   GET  /api/admin/corporate-certificates?entity_id=…   — list
 *   POST /api/admin/corporate-certificates/upsert        — upsert
 *   POST /api/admin/corporate-certificates/{id}/archive  — archive
 *
 * No new authority, no fabricated defaults. Empty Owner-controlled
 * fields (e.g. telephone) remain empty and are NEVER auto-populated
 * from client-side literals.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, Save, ShieldCheck, Clock, RefreshCw, Info, Plus, Pencil, Trash2, X, Eye } from 'lucide-react';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from '../../hooks/use-toast';
import DateField from '../../components/DateField';

const FIELDS = [
  ['commercial_title', 'Commercial title'],
  ['brand', 'Brand'],
  ['tax_office', 'Tax office'],
  ['tax_id', 'Tax ID (VKN)'],
  ['trade_registry_number', 'Trade registry no.'],
  ['mersis_number', 'MERSİS number'],
  ['chamber', 'Chamber'],
  ['chamber_registry_number', 'Chamber registry no.'],
  ['registered_address', 'Registered address'],
  ['general_email', 'General enquiries email'],
  ['kvkk_contact_email', 'KVKK contact email'],
  ['kep_email', 'KEP address (leave empty if not yet granted)'],
  ['phone', 'Telephone (leave empty if not yet assigned)'],
];

export default function CorporateIdentityAdmin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [entity, setEntity] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [form, setForm] = useState({});
  const [kepStatus, setKepStatus] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/legal-entities');
      const list = res?.data?.entities || res?.data || [];
      const treatxEntity = Array.isArray(list)
        ? (list.find(e => e.entity_id === 'treatx-tr') || list[0])
        : null;
      if (treatxEntity) {
        setEntity(treatxEntity);
        const seed = {};
        FIELDS.forEach(([k]) => { seed[k] = treatxEntity[k] || ''; });
        setForm(seed);
        setKepStatus(treatxEntity.kep_status || '');
      }
      const cRes = await api.get('/admin/corporate-certificates', {
        params: { entity_id: 'treatx-tr' },
      }).catch(() => null);
      const cList = cRes?.data?.certificates || [];
      setCertificates(cList);
    } catch (e) {
      toast({ title: 'Load failed', description: String(e?.message || e), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onField = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const onSave = async () => {
    if (!entity) return;
    setSaving(true);
    try {
      const payload = {};
      FIELDS.forEach(([k]) => {
        const v = (form[k] || '').trim();
        payload[k] = v || null;   // empty → null (canonical "unset")
      });
      if (kepStatus) payload.kep_status = kepStatus;
      await api.put(`/admin/legal-entities/${entity.entity_id}`, payload);
      toast({ title: 'Saved', description: 'Corporate identity updated.' });
      await load();
    } catch (e) {
      toast({ title: 'Save failed', description: String(e?.response?.data?.detail || e?.message || e), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const flipCertStatus = async (cert, newStatus) => {
    try {
      await api.put(`/admin/corporate-certificates/${cert.certificate_id}`, {
        status: newStatus,
      });
      await load();
    } catch (e) {
      toast({ title: 'Update failed', description: String(e?.message || e), variant: 'destructive' });
    }
  };

  // ── TREATX-OWNER-UAT-CERTIFICATES-CMS-01 ──────────────────────────
  // Full canonical CMS on `treatx_corporate_certificates`. Add / Edit /
  // Archive / delete-when-safe / status / metadata / image URL / PDF URL /
  // preview / surface selection. No parallel model.
  const [certEditor, setCertEditor] = useState(null);       // certificate being edited (or `{ __new: true }`)
  const [certPreview, setCertPreview] = useState(null);     // certificate being previewed
  const [certSaving, setCertSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const openNewCert = () => setCertEditor({
    __new: true,
    certificate_id: '',
    entity_id: entity?.entity_id || 'treatx-tr',
    document_type: 'iso_certificate',
    public_title: '',
    issuing_authority: '',
    certificate_number: '',
    scope: '',
    issue_date: '',
    valid_until: '',
    status: 'application_in_progress',
    asset_image_url: '',
    asset_pdf_url: '',
    is_public: false,
    display_surfaces: [],
    display_order: 100,
  });

  const openEditCert = (cert) => setCertEditor({
    __new: false,
    ...cert,
    display_surfaces: cert.display_surfaces || [],
  });

  const saveCert = async () => {
    if (!certEditor) return;
    const c = certEditor;
    if (!c.certificate_id || !c.public_title || !c.document_type) {
      toast({ title: 'Missing fields', description: 'certificate_id, public_title and document_type are required.', variant: 'destructive' });
      return;
    }
    setCertSaving(true);
    try {
      if (c.__new) {
        // POST is upsert-by-id on the canonical route.
        const { __new, ...body } = c;
        await api.post('/admin/corporate-certificates', body);
      } else {
        const { certificate_id, __new, created_at, updated_at, entity_id: _ei, ...updates } = c;
        await api.put(`/admin/corporate-certificates/${certificate_id}`, updates);
      }
      toast({ title: 'Saved', description: 'Certificate updated.' });
      setCertEditor(null);
      await load();
    } catch (e) {
      toast({ title: 'Save failed', description: String(e?.response?.data?.detail || e?.message || e), variant: 'destructive' });
    } finally {
      setCertSaving(false);
    }
  };

  const archiveCert = async (cert) => {
    // Archiving on this SSOT means status='suspended' — canonical field
    // already supports it; no data destruction. If the record is not
    // public anywhere, deletion is also offered as a secondary action.
    try {
      await api.put(`/admin/corporate-certificates/${cert.certificate_id}`, {
        status: 'suspended', is_public: false,
      });
      toast({ title: 'Archived', description: `${cert.public_title} archived (status=suspended, is_public=false).` });
      await load();
    } catch (e) {
      toast({ title: 'Archive failed', description: String(e?.message || e), variant: 'destructive' });
    }
  };

  const deleteCert = async (cert) => {
    if (!window.confirm(`Permanently delete ${cert.public_title}? This cannot be undone. Prefer Archive if in doubt.`)) return;
    try {
      await api.delete(`/admin/corporate-certificates/${cert.certificate_id}`);
      toast({ title: 'Deleted', description: cert.certificate_id });
      await load();
    } catch (e) {
      toast({ title: 'Delete failed', description: String(e?.message || e), variant: 'destructive' });
    }
  };

  const toggleSurface = (surface) => {
    if (!certEditor) return;
    const cur = new Set(certEditor.display_surfaces || []);
    if (cur.has(surface)) cur.delete(surface); else cur.add(surface);
    setCertEditor({ ...certEditor, display_surfaces: Array.from(cur) });
  };

  const isArchived = (c) => c.status === 'suspended' || c.status === 'revoked' || c.status === 'expired';
  const visibleCertificates = showArchived
    ? certificates
    : certificates.filter((c) => !isArchived(c));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500 gap-2">
        <Loader2 className="h-5 w-5 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="corporate-identity-admin">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/treatx-admin/dashboard')}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <h1 className="text-2xl font-bold text-slate-900">Corporate Identity</h1>
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="corp-admin-refresh">
            <RefreshCw className="h-4 w-4 mr-1" /> Reload
          </Button>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-emerald-600" /> Legal entity
            </CardTitle>
            <CardDescription>
              Canonical `legal_entities.treatx-tr`. Missing fields must remain empty until the Owner
              receives the real value. Nothing on this screen is auto-filled from marketing copy.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {FIELDS.map(([k, label]) => (
              <div key={k}>
                <Label className="text-xs uppercase tracking-wide text-slate-500">{label}</Label>
                <Input
                  value={form[k] || ''}
                  onChange={onField(k)}
                  placeholder=""
                  data-testid={`corp-admin-input-${k}`}
                />
              </div>
            ))}
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wide text-slate-500">KEP status</Label>
              <select
                value={kepStatus}
                onChange={(e) => setKepStatus(e.target.value)}
                className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                data-testid="corp-admin-kep-status"
              >
                <option value="">— unset —</option>
                <option value="application_in_progress">application_in_progress</option>
                <option value="granted">granted</option>
                <option value="not_applicable">not_applicable</option>
              </select>
              <p className="text-xs text-slate-500 mt-1 flex items-start gap-1">
                <Info className="h-3.5 w-3.5 mt-0.5" />
                When KEP is granted, set the address in the &quot;KEP address&quot; field above and flip status
                to <code>granted</code>. The public About page will reflect the change immediately.
              </p>
            </div>
          </CardContent>
          <div className="px-6 pb-6 flex justify-end">
            <Button
              onClick={onSave}
              disabled={saving}
              data-testid="corp-admin-save"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
              Save changes
            </Button>
          </div>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-start justify-between">
            <div>
              <CardTitle>Certificates &amp; Authorizations</CardTitle>
              <CardDescription>
                Canonical <code>treatx_corporate_certificates</code>. Add, edit, archive and preview
                certificates directly here. Public About / Homepage surfaces derive from this SSOT —
                changes are reflected immediately on the public site.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1 text-xs text-slate-600">
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                  data-testid="corp-admin-show-archived"
                />
                Show archived
              </label>
              <Button size="sm" onClick={openNewCert} data-testid="corp-admin-cert-add">
                <Plus className="h-4 w-4 mr-1" /> Add certificate
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {visibleCertificates.length === 0 && (
              <div className="text-sm text-slate-500" data-testid="corp-admin-certs-empty">
                No certificates loaded. Click Add to create one, or Reload if you just seeded.
              </div>
            )}
            {visibleCertificates.map(c => (
              <div
                key={c.certificate_id}
                className="flex items-center justify-between gap-3 border border-slate-200 rounded-md px-4 py-3"
                data-testid={`corp-admin-cert-row-${c.certificate_id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-900 truncate">{c.public_title}</div>
                  <div className="text-xs text-slate-500 mt-0.5 truncate">
                    {c.issuing_authority || '—'} · {c.document_type} · surfaces:{' '}
                    {(c.display_surfaces || []).join(', ') || '—'}
                    {c.certificate_number ? ` · #${c.certificate_number}` : ''}
                  </div>
                  {(c.asset_image_url || c.asset_pdf_url) && (
                    <div className="text-xs text-emerald-700 mt-1 flex gap-2">
                      {c.asset_image_url && <span>image ✓</span>}
                      {c.asset_pdf_url && <span>pdf ✓</span>}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium
                    ${c.status === 'active'
                      ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                      : isArchived(c)
                        ? 'bg-slate-100 text-slate-600 border border-slate-200'
                        : 'bg-amber-100 text-amber-800 border border-amber-200'}`}
                    data-testid={`corp-admin-cert-status-${c.certificate_id}`}
                  >
                    {c.status === 'active'
                      ? <><ShieldCheck className="h-3 w-3" /> granted</>
                      : <><Clock className="h-3 w-3" /> {c.status}</>}
                  </span>
                  {(c.asset_image_url || c.asset_pdf_url) && (
                    <Button size="sm" variant="ghost" onClick={() => setCertPreview(c)} data-testid={`corp-admin-cert-preview-${c.certificate_id}`}>
                      <Eye className="h-4 w-4" />
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => openEditCert(c)} data-testid={`corp-admin-cert-edit-${c.certificate_id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  {c.status !== 'active' && (
                    <Button size="sm" variant="outline"
                      onClick={() => flipCertStatus(c, 'active')}
                      data-testid={`corp-admin-cert-grant-${c.certificate_id}`}
                    >
                      Mark granted
                    </Button>
                  )}
                  {c.status === 'active' && (
                    <Button size="sm" variant="outline"
                      onClick={() => flipCertStatus(c, 'application_in_progress')}
                      data-testid={`corp-admin-cert-revert-${c.certificate_id}`}
                    >
                      Revert
                    </Button>
                  )}
                  {!isArchived(c) ? (
                    <Button size="sm" variant="ghost" className="text-slate-500" onClick={() => archiveCert(c)} data-testid={`corp-admin-cert-archive-${c.certificate_id}`}>
                      Archive
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteCert(c)} data-testid={`corp-admin-cert-delete-${c.certificate_id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* ── Editor dialog ─────────────────────────────────────────── */}
      <Dialog open={!!certEditor} onOpenChange={(open) => { if (!open) setCertEditor(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="corp-admin-cert-editor">
          <DialogHeader>
            <DialogTitle>{certEditor?.__new ? 'Add certificate / authorization' : `Edit ${certEditor?.public_title || ''}`}</DialogTitle>
          </DialogHeader>
          {certEditor && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wide text-slate-500">Certificate ID (slug)</Label>
                <Input value={certEditor.certificate_id}
                  disabled={!certEditor.__new}
                  onChange={(e) => setCertEditor({ ...certEditor, certificate_id: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,'-') })}
                  placeholder="e.g. iso-9001"
                  data-testid="corp-admin-cert-field-id" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wide text-slate-500">Public title</Label>
                <Input value={certEditor.public_title || ''} onChange={(e) => setCertEditor({ ...certEditor, public_title: e.target.value })} data-testid="corp-admin-cert-field-title" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500">Document type</Label>
                <select value={certEditor.document_type || 'iso_certificate'} onChange={(e) => setCertEditor({ ...certEditor, document_type: e.target.value })} className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="corp-admin-cert-field-type">
                  <option value="iso_certificate">iso_certificate</option>
                  <option value="authorization">authorization</option>
                  <option value="license">license</option>
                  <option value="accreditation">accreditation</option>
                  <option value="registration">registration</option>
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500">Status</Label>
                <select value={certEditor.status || 'application_in_progress'} onChange={(e) => setCertEditor({ ...certEditor, status: e.target.value })} className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="corp-admin-cert-field-status">
                  <option value="active">active (granted)</option>
                  <option value="application_in_progress">application_in_progress</option>
                  <option value="expired">expired</option>
                  <option value="revoked">revoked</option>
                  <option value="suspended">suspended (archived)</option>
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500">Issuing authority</Label>
                <Input value={certEditor.issuing_authority || ''} onChange={(e) => setCertEditor({ ...certEditor, issuing_authority: e.target.value })} data-testid="corp-admin-cert-field-issuer" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500">Certificate number</Label>
                <Input value={certEditor.certificate_number || ''} onChange={(e) => setCertEditor({ ...certEditor, certificate_number: e.target.value })} data-testid="corp-admin-cert-field-number" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500">Issue date</Label>
                <DateField
                  value={certEditor.issue_date || ''}
                  onChange={(iso) => setCertEditor({ ...certEditor, issue_date: iso })}
                  data-testid="corp-admin-cert-field-issue-date"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500">Valid until</Label>
                <DateField
                  value={certEditor.valid_until || ''}
                  onChange={(iso) => setCertEditor({ ...certEditor, valid_until: iso })}
                  data-testid="corp-admin-cert-field-valid-until"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wide text-slate-500">Scope</Label>
                <Input value={certEditor.scope || ''} onChange={(e) => setCertEditor({ ...certEditor, scope: e.target.value })} placeholder="e.g. Quality management" data-testid="corp-admin-cert-field-scope" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wide text-slate-500">Certificate image URL</Label>
                <div className="flex gap-2">
                  <Input value={certEditor.asset_image_url || ''} onChange={(e) => setCertEditor({ ...certEditor, asset_image_url: e.target.value })} placeholder="https://…" data-testid="corp-admin-cert-field-image-url" />
                  <label className="inline-flex items-center h-10 px-3 rounded-md border border-slate-200 bg-white text-sm cursor-pointer whitespace-nowrap" data-testid="corp-admin-cert-upload-image-btn">
                    Upload…
                    <input type="file" accept="image/*" className="hidden"
                      onChange={async (e) => {
                        const f = e.target.files?.[0]; if (!f) return;
                        const fd = new FormData(); fd.append('file', f);
                        try {
                          const r = await api.post('/admin/corporate-certificates/upload-asset', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                          if (r?.data?.public_url) {
                            setCertEditor((c) => ({ ...c, asset_image_url: r.data.public_url }));
                            toast({ title: 'Image uploaded', description: r.data.public_url });
                          }
                        } catch (err) { toast({ title: 'Upload failed', description: String(err?.response?.data?.detail || err?.message || err), variant: 'destructive' }); }
                        e.target.value = '';
                      }} />
                  </label>
                </div>
                <p className="text-xs text-slate-500 mt-1">Upload a JPG/PNG/WEBP directly, or paste an existing URL. Max 10&nbsp;MB.</p>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wide text-slate-500">Certificate PDF URL</Label>
                <div className="flex gap-2">
                  <Input value={certEditor.asset_pdf_url || ''} onChange={(e) => setCertEditor({ ...certEditor, asset_pdf_url: e.target.value })} placeholder="https://…" data-testid="corp-admin-cert-field-pdf-url" />
                  <label className="inline-flex items-center h-10 px-3 rounded-md border border-slate-200 bg-white text-sm cursor-pointer whitespace-nowrap" data-testid="corp-admin-cert-upload-pdf-btn">
                    Upload…
                    <input type="file" accept="application/pdf" className="hidden"
                      onChange={async (e) => {
                        const f = e.target.files?.[0]; if (!f) return;
                        const fd = new FormData(); fd.append('file', f);
                        try {
                          const r = await api.post('/admin/corporate-certificates/upload-asset', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                          if (r?.data?.public_url) {
                            setCertEditor((c) => ({ ...c, asset_pdf_url: r.data.public_url }));
                            toast({ title: 'PDF uploaded', description: r.data.public_url });
                          }
                        } catch (err) { toast({ title: 'Upload failed', description: String(err?.response?.data?.detail || err?.message || err), variant: 'destructive' }); }
                        e.target.value = '';
                      }} />
                  </label>
                </div>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wide text-slate-500">Display surfaces</Label>
                <div className="flex gap-3 mt-1">
                  {['homepage', 'about', 'footer'].map((s) => (
                    <label key={s} className="flex items-center gap-1 text-sm" data-testid={`corp-admin-cert-surface-${s}`}>
                      <input type="checkbox" checked={(certEditor.display_surfaces || []).includes(s)} onChange={() => toggleSurface(s)} />
                      {s}
                    </label>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-1">A single record may appear on multiple public surfaces. Uncheck all to keep the record but hide from public.</p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500">Display order</Label>
                <Input type="number" value={certEditor.display_order ?? 100} onChange={(e) => setCertEditor({ ...certEditor, display_order: Number(e.target.value) })} data-testid="corp-admin-cert-field-order" />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm" data-testid="corp-admin-cert-field-is-public">
                  <input type="checkbox" checked={!!certEditor.is_public} onChange={(e) => setCertEditor({ ...certEditor, is_public: e.target.checked })} />
                  <span>Publish (is_public)</span>
                </label>
              </div>
              <div className="md:col-span-2 flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => setCertEditor(null)} data-testid="corp-admin-cert-editor-cancel">Cancel</Button>
                <Button onClick={saveCert} disabled={certSaving} data-testid="corp-admin-cert-editor-save">
                  {certSaving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                  {certEditor.__new ? 'Create' : 'Save changes'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Preview / lightbox ─────────────────────────────────── */}
      <Dialog open={!!certPreview} onOpenChange={(open) => { if (!open) setCertPreview(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="corp-admin-cert-preview">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between pr-6">
              <span>{certPreview?.public_title}</span>
              <Button variant="ghost" size="sm" onClick={() => setCertPreview(null)}><X className="h-4 w-4" /></Button>
            </DialogTitle>
          </DialogHeader>
          {certPreview && (
            <div className="space-y-3">
              <div className="text-xs text-slate-500">
                {certPreview.issuing_authority || '—'} · {certPreview.document_type} · status <code>{certPreview.status}</code>
                {certPreview.certificate_number ? ` · #${certPreview.certificate_number}` : ''}
              </div>
              {certPreview.asset_image_url && (
                <img src={certPreview.asset_image_url} alt={certPreview.public_title} className="w-full max-h-[60vh] object-contain bg-slate-50 rounded-md" data-testid="corp-admin-cert-preview-image" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              )}
              {certPreview.asset_pdf_url && (
                <a href={certPreview.asset_pdf_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-700 underline" data-testid="corp-admin-cert-preview-pdf">
                  Open certificate PDF ↗
                </a>
              )}
              {!certPreview.asset_image_url && !certPreview.asset_pdf_url && (
                <div className="text-sm text-slate-500">No image or PDF attached to this record.</div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
